import { AfterViewInit, Component, ElementRef, OnInit, inject, viewChild } from '@angular/core';
import { EnvironmentService } from 'src/app/core/services/environment.service';
import { LanguageService } from 'src/app/core/services/language.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { SideNavService } from './side-nav.service';
import { SsApiService } from '../../../services/api/ss-api.service';
import { PlatformService } from '../../../services/platform.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AccordionDirective } from '../../directives/accordion.directive';
import { NavigationStart, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { WindowService } from '../../../services/window.service';
import { ModalService } from '../../../modal/modal.service';
import { of } from 'rxjs';
import { delay, filter, tap } from 'rxjs/operators';
import { WrSocketsService } from '../../../services/wr-sockets.service';
import { ZendeskChatService } from '../../../services/zendesk/zendesk-chat.service';
import { LotteryService } from '../../../services/lottery.service';
import { InstallAppService } from '../../../services/install-app.service';
import { BonusStoreService } from '../../../services/bonus-store.service';
import { TranslatePipe } from '../../../services/translation/translate.pipe';
import { RouterLinkDirective } from '../../directives/router-link.directive';
import { BtnComponent } from '../../components/btn/btn.component';
import { HeaderBalanceComponent } from '../../components/header-balance/header-balance.component';
import { ThemeSelectorComponent } from '../../components/theme-selector/theme-selector.component';
import { LocalesSelectorComponent } from '../../components/locales-selector/locales-selector.component';
import { AsyncPipe, NgClass } from '@angular/common';
import { ClickOutsideDirective } from '../../directives/click-outside.directive';
import { AppShellNoSSRDirective } from 'ngx-unificator/directives';
import { SeoService } from '../../../services/seo.service';
import {
  LOCAL_STORAGE_FRAUD_KEY_CASHOUT,
  LOCAL_STORAGE_FRAUD_KEY_DEPOSIT,
} from '../../../services/fraud-check.service';
import { LocalstorageService } from 'ngx-unificator/services';
import { SummerService } from '../../../../page/promo/summer/summer.service';

@UntilDestroy()
@Component({
  selector: 'app-side-menu',
  templateUrl: './side-menu.component.html',
  styleUrls: ['./side-menu.component.scss'],
  standalone: true,
  imports: [
    ClickOutsideDirective,
    NgClass,
    LocalesSelectorComponent,
    ThemeSelectorComponent,
    HeaderBalanceComponent,
    BtnComponent,
    RouterLink,
    RouterLinkDirective,
    RouterLinkActive,
    AccordionDirective,
    AsyncPipe,
    TranslatePipe,
    AppShellNoSSRDirective,
  ],
})
export class SideMenuComponent implements OnInit, AfterViewInit {
  lang = inject(LanguageService);
  sidebar = inject(SideNavService);
  env = inject(EnvironmentService);
  user = inject(UserService);
  window = inject(WindowService);
  zendesk = inject(ZendeskChatService);
  installApp = inject(InstallAppService);
  bonusStore = inject(BonusStoreService);
  seo = inject(SeoService);
  summer = inject(SummerService);

  private _ssApi = inject(SsApiService);
  private _platform = inject(PlatformService);
  private _router = inject(Router);
  private _wrSocket = inject(WrSocketsService);
  private _lottery = inject(LotteryService);
  private _storage = inject(LocalstorageService);


  readonly accordionGames = viewChild('accordionGames', { read: AccordionDirective });
  readonly accordionMore = viewChild('accordionMore', { read: AccordionDirective });

  readonly submenuMore = viewChild<ElementRef>('submenuMore');
  readonly submenuGames = viewChild<ElementRef>('submenuGames');

  /**
   * More submenu items
   */
  public moreMenu = {
    auth: [
      { title: 'link.profile', link: `profile` },
      { title: 'link.documents', link: `documents` },
    ],
    notAuth: [
      { title: 'link.vip-program', link: `vip-program` },
      { title: 'link.blog', link: `blog` },
      { title: 'link.banking', link: `banking` },
      { title: 'link.user-lottery', link: `lottery`, hidden: true },
      { title: 'link.about-us', link: `about` },
      { title: 'link.responsible-gaming', link: `responsible-gaming` },
      { title: 'link.faq', link: `faq` },
      { title: 'link.terms-and-cond', link: `terms-and-conditions` },
      { title: 'link.privacy-policy', link: `privacy-policy` },
      { title: 'link.cookie-policy', link: `cookie-policy` },
    ],
  };

  /**
   * Games submenu items
   */
  public gamesMenu = [
    { title: 'link.fast-play', link: 'fast-play' },
    { title: 'link.live', link: 'live' },
    { title: 'link.slots', link: 'slots' },
    { title: 'link.jackpot-games', link: 'jackpot' },
    { title: 'link.table-games', link: 'table' },
  ];

  ngOnInit(): void {
    this._router.events.pipe(
      filter((event: any) => event instanceof NavigationStart || this._isSameRoute(event.url)),
      untilDestroyed(this),
    ).subscribe(() => {
      this.closeMenu();
    });
  }

  ngAfterViewInit() {
    this._checkUrlAndToggleSubmenu();
    this._checkLotteryLinkVisibility();
  }

  /**
   * Compares the current URL with a target URL and returns a boolean
   * @param {string} targetUrl
   * @returns A boolean value is being returned, indicating whether the current URL is the same as the
   * target URL.
   */
  private _isSameRoute(targetUrl: string): boolean {
    const currentUrl = this._router.url;
    return currentUrl === targetUrl;
  }

  /**
   * Check url and toggle submenu
   * @private
   */
  private _checkUrlAndToggleSubmenu() {
    const moreMenu = this.moreMenu.auth.concat(this.moreMenu.notAuth).some(item => {
      return this._router.url.includes(item.link);
    });
    const gamesMenu = this.gamesMenu.some(item => {
      return this._router.url.includes(item.link);
    });
    this._toggleSubmenu(moreMenu, this.sidebar.isMoreSubmenu, this.accordionMore());
    this._toggleSubmenu(gamesMenu, this.sidebar.isGamesSubmenu, this.accordionGames());
  }

  /**
   * Toggle submenu fn
   * @param isSubMenu
   * @param isTypeSubmenu
   * @param accordionInstance
   * @private
   */
  private _toggleSubmenu(isSubMenu: boolean, isTypeSubmenu: any, accordionInstance: AccordionDirective) {
    if (!isSubMenu) {
      this.sidebar[isTypeSubmenu] = false;
      accordionInstance.close();
    } else {
      this.sidebar[isTypeSubmenu] = true;
      accordionInstance.open();
    }
  }

  installPwa() {
    this._router.navigateByUrl('mobile-app');
  }

  async openInformer() {
    // const component = await import('../../../modal/components/lazy/')
    // this._modal.open('PWA_INSTALL');
  }

  /**
   * Click on submenu games
   */
  clickSubmenuGames() {
    this.sidebar.isGamesSubmenu = !this.sidebar.isGamesSubmenu;
    this.sidebar.isMoreSubmenu = false;
    this.accordionMore().close();
    this._scrollSubmenu(this.submenuGames());
  }

  /**
   * Click on submenu more
   */
  clickSubmenuMore() {
    this.sidebar.isMoreSubmenu = !this.sidebar.isMoreSubmenu;
    this.sidebar.isGamesSubmenu = false;
    this.accordionGames().close();
    this._scrollSubmenu(this.submenuMore());
  }

  /**
   * Close menu
   */
  closeMenu() {
    if (this.sidebar.isOpen) {
      this.sidebar.close();
    }
  }

  /**
   * Logout
   */
  onLogoutClick(): void {
    this._storage.clearItem(LOCAL_STORAGE_FRAUD_KEY_DEPOSIT);
    this._storage.clearItem(LOCAL_STORAGE_FRAUD_KEY_CASHOUT);
    this._wrSocket.sendEventLogout();
    this.sidebar.close();
    this._ssApi.usersSignOut().subscribe(() => {
      if (this._platform.isBrowser) {
        window.location.reload();
      }
    });
  }

  /**
   * Auto scroll submenu
   * @param el
   * @private
   */
  private _scrollSubmenu(el: ElementRef) {
    of(true).pipe(
      untilDestroyed(this),
      delay(100),
      tap(() => {
        el.nativeElement.scrollIntoView({ behavior: 'smooth' });
      }),
    ).subscribe();
  }

  private _checkLotteryLinkVisibility() {
    this._lottery.checkLotteryLinkVisibility$().subscribe(lotteries => {
      const lotteryLinkIdx = this.moreMenu.notAuth.findIndex(el => el.link === 'lottery');
      this.moreMenu.notAuth[lotteryLinkIdx].hidden = !lotteries;
    });
  }
}
